import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  Search,
  NotificationsNone,
  AccountCircle,
  KeyboardArrowDown,
  Login,
  Logout,
  HelpOutline,
  Settings,
  WorkspacePremium,
  LightMode,
  DarkMode,
  Menu as MenuIcon,
} from "@mui/icons-material";

interface NavbarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  userName: string | null | undefined;
  userPlan: "Free" | "Pro" | "Team";
  onSignIn: (name: string) => void;
  onSignOut: () => void;
  onMenuOpen?: () => void;
}

export default function Navbar({
  currentPage,
  onPageChange,
  darkMode,
  onToggleDarkMode,
  userName,
  userPlan,
  onSignIn,
  onSignOut,
  onMenuOpen,
}: NavbarProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);
  const openNotifications = Boolean(notificationAnchorEl);

  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: "Welcome to CoWrite",
      message: "Get started with our AI tools.",
      time: "2m ago",
      unread: true,
    },
    {
      id: 2,
      title: "Plan Update",
      message: "You are currently on the Free Plan.",
      time: "1h ago",
      unread: true,
    },
    {
      id: 3,
      title: "New Feature",
      message: "Try the new Image Creator!",
      time: "1d ago",
      unread: false,
    },
  ]);

  const unreadCount = notifications.filter((n) => n.unread).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
  };

  const handleLoginClick = () => {
    onPageChange("signin");
  };

  const handleLogoutClick = () => {
    onSignOut();
    handleClose();
  };

  return (
    <Box
      sx={{
        height: { xs: 60, md: 80 },
        display: "flex",
        justifyContent: "center",
        bgcolor: darkMode ? "rgba(3, 3, 3, 0.92)" : "rgba(255, 255, 255, 0.97)",
        borderBottom: "1px solid",
        borderColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)",
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backdropFilter: "blur(12px)",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          width: "100%",
          mx: "auto",
          px: { xs: 2, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {/* LEFT: Hamburger (mobile) + Breadcrumb (desktop) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {/* Hamburger — mobile only */}
          <IconButton
            onClick={onMenuOpen}
            size="small"
            sx={{
              display: { xs: "flex", md: "none" },
              color: darkMode ? "#9ca3af" : "#64748b",
              flexShrink: 0,
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Page name — mobile center */}
          <Typography
            sx={{
              display: { xs: "block", md: "none" },
              color: darkMode ? "white" : "black",
              fontWeight: 700,
              fontSize: "16px",
              textTransform: "capitalize",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentPage.replace(/-/g, " ")}
          </Typography>

          {/* Breadcrumb — desktop only */}
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: darkMode ? "#4b5563" : "#64748b", fontWeight: 500, fontSize: "15px" }}>
              Tools
            </Typography>
            <Typography sx={{ color: darkMode ? "#4b5563" : "#64748b", fontSize: "15px" }}>/</Typography>
            <Typography sx={{ color: darkMode ? "white" : "black", fontWeight: 600, fontSize: "15px", textTransform: "capitalize" }}>
              {currentPage.replace(/-/g, " ")}
            </Typography>
          </Box>
        </Box>

        {/* CENTER: Search (desktop only) */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            bgcolor: darkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
            px: 2, py: 1,
            borderRadius: "14px",
            width: 360,
            border: "1px solid",
            borderColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            transition: "all 0.2s",
            "&:focus-within": {
              bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "white",
              borderColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
            },
          }}
        >
          <Search sx={{ color: darkMode ? "#4b5563" : "#64748b", fontSize: 22, mr: 1.5 }} />
          <InputBase
            placeholder="Search for tools..."
            sx={{
              color: darkMode ? "white" : "black",
              fontSize: "15px",
              "& .MuiInputBase-input::placeholder": { color: darkMode ? "#4b5563" : "#64748b", opacity: 1 },
            }}
            fullWidth
          />
        </Box>

        {/* RIGHT: Actions */}
        <Stack direction="row" spacing={{ xs: 0.5, md: 3 }} alignItems="center">
          {/* Theme toggle — desktop only */}
          <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"}>
            <IconButton
              size="small"
              onClick={onToggleDarkMode}
              sx={{
                display: { xs: "none", md: "flex" },
                color: darkMode ? "#4b5563" : "#64748b",
                "&:hover": { color: darkMode ? "white" : "black" },
              }}
            >
              {darkMode ? <LightMode fontSize="medium" /> : <DarkMode fontSize="medium" />}
            </IconButton>
          </Tooltip>

          <IconButton
            size="small"
            onClick={handleNotificationClick}
            sx={{
              color: darkMode ? "#4b5563" : "#64748b",
              "&:hover": { color: darkMode ? "white" : "black" },
            }}
          >
            <Badge variant="dot" color="primary" invisible={unreadCount === 0}>
              <NotificationsNone fontSize="medium" />
            </Badge>
          </IconButton>

          <Popover
            open={openNotifications}
            anchorEl={notificationAnchorEl}
            onClose={handleNotificationClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                width: 320,
                maxHeight: 400,
                bgcolor: darkMode ? "#0a0a0a" : "#ffffff",
                border: "1px solid",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
                borderRadius: "16px",
                color: darkMode ? "white" : "black",
                boxShadow: darkMode
                  ? "0 10px 40px -10px rgba(0,0,0,0.5)"
                  : "0 10px 40px -10px rgba(0,0,0,0.1)",
                mt: 1.5,
              },
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Notifications
              </Typography>
              <Typography
                variant="caption"
                onClick={handleMarkAllAsRead}
                sx={{
                  color: "#3b82f6",
                  cursor: "pointer",
                  fontWeight: 600,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Mark all as read
              </Typography>
            </Box>

            {/* List */}
            <List sx={{ p: 0 }}>
              {notifications.map((notif) => (
                <ListItem
                  key={notif.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.03)",
                    bgcolor: notif.unread
                      ? darkMode
                        ? "rgba(59, 130, 246, 0.05)"
                        : "rgba(59, 130, 246, 0.03)"
                      : "transparent",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: darkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      mr: 2,
                      display: "flex",
                      alignItems: "center",
                      minWidth: 8,
                    }}
                  >
                    {notif.unread && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#3b82f6",
                        }}
                      />
                    )}
                  </Box>
                  <ListItemText
                    primary={notif.title}
                    secondary={notif.message}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: notif.unread ? 600 : 500,
                      color: darkMode ? "white" : "black",
                      fontSize: "13px",
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: darkMode ? "#9ca3af" : "#6b7280",
                      sx: { display: "block", mt: 0.5 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#6b7280",
                      fontSize: "10px",
                      minWidth: "max-content",
                      ml: 1,
                    }}
                  >
                    {notif.time}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Popover>

          <Box
            sx={{
              width: "1px",
              height: 28,
              bgcolor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            }}
          />

          {userName ? (
            <Box
              onClick={handleClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                cursor: "pointer",
                pl: 1,
                "&:hover opacity": 0.8,
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#3b82f6",
                  fontSize: "15px",
                  fontWeight: 600,
                }}
              >
                {userName.charAt(0)}
              </Avatar>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: darkMode ? "white" : "black",
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: darkMode ? "#4b5563" : "#64748b",
                    display: "block",
                    lineHeight: 1,
                    fontSize: "11px",
                  }}
                >
                  {userPlan} Plan
                </Typography>
              </Box>
              <KeyboardArrowDown
                sx={{ color: darkMode ? "#4b5563" : "#64748b", fontSize: 20 }}
              />
            </Box>
          ) : (
            <>
              {/* Mobile: compact icon button */}
              <IconButton
                onClick={handleLoginClick}
                size="small"
                sx={{
                  display: { xs: "flex", md: "none" },
                  bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                  color: darkMode ? "white" : "black",
                  borderRadius: "10px",
                  p: 1,
                  "&:hover": { bgcolor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)" },
                }}
              >
                <Login fontSize="small" />
              </IconButton>
              {/* Desktop: full button */}
              <Button
                variant="contained"
                onClick={handleLoginClick}
                startIcon={<Login />}
                sx={{
                  display: { xs: "none", md: "flex" },
                  bgcolor: darkMode ? "white" : "primary.main",
                  color: darkMode ? "black" : "white",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "15px",
                  px: 4,
                  height: 44,
                  "&:hover": { bgcolor: darkMode ? "#e5e7eb" : "primary.dark" },
                }}
              >
                Sign In
              </Button>
            </>
          )}

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            elevation={0}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              mt: 1.5,
              "& .MuiPaper-root": {
                bgcolor: darkMode ? "#0a0a0a" : "#ffffff",
                border: "1px solid",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
                borderRadius: "16px",
                width: 200,
                color: darkMode ? "white" : "black",
                boxShadow: darkMode
                  ? "0 10px 40px -10px rgba(0,0,0,0.5)"
                  : "0 10px 40px -10px rgba(0,0,0,0.1)",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                onPageChange("settings");
                handleClose();
              }}
              sx={{
                py: 1.5,
                "&:hover": {
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Settings fontSize="small" sx={{ color: "#4b5563" }} />
                <Typography variant="body2">Settings</Typography>
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => {
                onPageChange("manage-plan");
                handleClose();
              }}
              sx={{
                py: 1.5,
                "&:hover": {
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <WorkspacePremium fontSize="small" sx={{ color: "#fbbf24" }} />
                <Typography variant="body2">Manage Plan</Typography>
              </Stack>
            </MenuItem>
            <Box
              sx={{
                height: "1px",
                width: "100%",
                bgcolor: darkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.05)",
                my: 1,
              }}
            />
            <MenuItem
              onClick={handleLogoutClick}
              sx={{
                py: 1.5,
                color: "#ef4444",
                "&:hover": { bgcolor: "rgba(239,68,68,0.05)" },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Logout fontSize="small" />
                <Typography variant="body2">Sign Out</Typography>
              </Stack>
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
    </Box>
  );
}
